import type { PlatformContent } from "./platform";

export const defaultPlatformContent: PlatformContent = {
  meta: {
    title: "Quintara Platform",
    description:
      "A multi-tenant business messaging platform built on the WhatsApp Business Platform Cloud API, CRM and commerce integrations, and Meta measurement.",
  },
  hero: {
    eyebrow: "Platform",
    heading: "Built directly on the WhatsApp Business Platform.",
    lede: "Quintara connects straight to Meta's Cloud API and Graph API. We don't resell another provider's connection, so we control template management, quality signals, throughput and webhook delivery ourselves.",
    note: "",
  },
  specs: [
    {
      eyebrow: "Messaging",
      heading: "Everything a WhatsApp conversation can carry.",
      items: [
        { title: "Templates and sessions", description: "Approved templates for business-initiated messages and free-form replies inside the 24-hour service window." },
        { title: "Interactive messages", description: "Reply buttons, list menus and call-to-action links." },
        { title: "WhatsApp Flows", description: "Multi-screen forms for bookings, sign-ups and surveys inside the chat." },
        { title: "Media", description: "Images, documents, audio, video and stickers." },
        { title: "Catalogs", description: "Single and multi-product messages from your synced catalog." },
        { title: "Conversation signals", description: "Reactions, quoted replies and typing indicators." },
        { title: "Payments", badge: "Roadmap", description: "Sequenced market by market as Meta makes it available." },
        { title: "Calling", badge: "Roadmap", description: "Voice calls from the business thread, where supported." },
      ],
    },
    {
      id: "ai",
      eyebrow: "AI and automation",
      heading: "Automation that knows when to stop.",
      intro: "Built and run by the Numbase AI lab.",
      items: [
        { title: "Dialect-aware agents", description: "Gulf, Levantine and Egyptian Arabic, plus English, French and Turkish." },
        { title: "Grounded on your data", description: "Each agent answers from your catalog, prices, policies and FAQs. Anything else goes to a fallback or a person." },
        { title: "Human handoff", description: "Triggered by customer request, low confidence, negative sentiment or your own keywords, with full history passed along." },
        { title: "Lead qualification", description: "Intent detection and qualifying questions, with structured leads written to your CRM." },
        { title: "Agent assist", description: "Suggested replies, conversation summaries and tagging for your human team." },
        { title: "Pre-send checks", description: "Template copy and audiences are reviewed against past block rates before a campaign runs." },
      ],
    },
    {
      eyebrow: "Integrations and measurement",
      heading: "Connected to where your revenue is recorded.",
      items: [
        { title: "CRM", description: "Salesforce, HubSpot and Zoho: lead creation, enrichment, activity logging and two-way status sync." },
        { title: "Commerce", description: "Salla, Zid and Shopify: catalog sync, abandoned carts, order and shipping updates." },
        { title: "Conversions API", description: "Server-side events, deduplicated against the Pixel, with hashed customer data and Event Match Quality monitoring." },
        { title: "Click-to-WhatsApp attribution", description: "Ad referrals captured at entry and tied through the conversation to the conversion." },
        { title: "Messaging analytics", description: "Template performance, conversation categories, cost per conversation and response times." },
        { title: "API, webhooks and MCP", description: "A REST API and webhooks for your systems, and an MCP server so your own AI agents can drive messaging." },
      ],
    },
    {
      id: "quality",
      eyebrow: "Quality and policy",
      heading: "We protect the channel, not just the send.",
      intro: "Poor quality hurts the business, its customers and WhatsApp. We treat enforcement as our job.",
      items: [
        { title: "Onboarding gate", description: "Business verification, presence checks and use-case review before any account can send in production." },
        { title: "Policy checks before submission", description: "Templates are checked against WhatsApp's commerce and messaging policies before they go to review." },
        { title: "Live quality monitoring", description: "Quality rating, block rate and report rate are watched per number. A downgrade throttles marketing sends and alerts the business." },
        { title: "Graduated intervention", description: "Advice first, then mandatory review, then suspension of marketing sends. Repeat or severe violations lead to offboarding." },
        { title: "Opt-in records", description: "Where and when each contact opted in is recorded and auditable." },
        { title: "Opt-out everywhere", description: "An opt-out applies immediately across every template and number in the account." },
      ],
    },
    {
      id: "security",
      eyebrow: "Infrastructure and security",
      heading: "Designed for carrier-grade reliability.",
      intro: "Hosted on AWS in the client's region, including UAE (me-central-1) and Bahrain (me-south-1) for Gulf clients.",
      items: [
        { title: "Tenant isolation", description: "Each WhatsApp Business Account sits in its own tenant boundary, with row-level security, separate encryption keys and scoped credentials." },
        { title: "Fair throughput", description: "Queues per account and per number, paced to each number's messaging tier, so one busy sender can't slow another." },
        { title: "No single point of failure", description: "Every tier spans at least three availability zones with automatic failover and point-in-time database recovery." },
        { title: "Credential security", description: "Encrypted secrets storage, secure token handling, TLS everywhere and encryption at rest." },
      ],
      table: {
        columns: ["Service target", "Value"],
        rows: [
          ["Platform availability", "99.9% monthly · 99.95% enterprise SLA"],
          ["API response time, p95", "Under 200 ms"],
          ["Dispatch to Meta, p95", "Under 500 ms"],
          ["Webhook processing, p95", "Under 1 s"],
          ["Critical incident response", "Under 15 min, 24/7"],
          ["Recovery objectives", "RTO 4 h · RPO 15 min"],
        ],
      },
    },
  ],
  ctaBand: {
    eyebrow: "Get in touch",
    heading: "Want a technical walkthrough?",
    lede: "Our solutions engineers can take your team through the architecture, integrations and onboarding plan.",
    buttonLabel: "Talk to our team",
    buttonTo: "/contact",
  },
};
